import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatMap, map } from 'rxjs/operators';
import { from } from 'rxjs';
import { SmtActionsTypes, SmtDownloadAllAction, SmtExcelExportAction } from '../actions/actions';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { DownloadAllDialog } from '../../dialogs/download-all/download-all.dialog';
import { ExcelExportDialog } from '../../dialogs/excel-export/excel-export.dialog';
import { ExcelExportWarningDialog } from '../../dialogs/excel-export/excel-export-warning.dialog';
import { DownloadSizeWarningDialog } from '../../dialogs/download-all/download-size-warning.dialog';
import { SMTLoadingDialogComponent } from '../../dialogs/loading/loading.dialog';
import { ActionDialogConfig, SmtActionsService } from '../../services/actions.service';
import { CustomListQueryBuilderService } from '../../services/custom-list-query-builder.service';
import { AldenService } from '../../services/alden.service';
import { APP_CONFIG_MAX_ITEMS_EXPORT, APP_CONFIG_MAX_SIZE_DOWNLOAD } from '../../models/alden-constants';
import { AppStore, SnackbarErrorAction } from '@alfresco/aca-shared/store';

@Injectable()
export class SMTActionsEffects {
  constructor(
    private actions$: Actions,
    private dialog: MatDialog,
    private aldenService: AldenService,
    private smtActionsService: SmtActionsService,
    private customListQueryBuilderService: CustomListQueryBuilderService,
    private store: Store<AppStore>,
  ) { }

  // Efecto para manejar la acción de exportar a Excel
  SmtExcelExportAction$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<SmtExcelExportAction>(SmtActionsTypes.SmtExcelExportAction),
        map((action) => {
          this.smtExcelExportActionDialog({
            'selection': action.selection,
            'totalItems': action.totalItems
          });
        })
      ),
    { dispatch: false }
  );

  // Efecto para manejar la acción de descargar al completo
  SmtDownloadAllAction$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<SmtDownloadAllAction>(SmtActionsTypes.SmtDownloadAllAction),
        concatMap((action) => from(
          this.smtDownloadAllActionDialog({
            selection: action.selection
          })
        ))
      ),
    { dispatch: false }
  );

  /**
   * Abre el cuadro de diálogo correspondiente a la exportación de datos.
   * Si se supera el límite configurado, muestra el aviso en lugar del diálogo de exportación.
   *
   * @param config - Configuración que contiene la selección de datos a exportar.
   * @returns Un MatDialogRef con la instancia del diálogo mostrado.
   */
  private smtExcelExportActionDialog(config: ActionDialogConfig): MatDialogRef<any> {
    if (this.exceedsExportLimit(config)) {
      return this.dialog.open(ExcelExportWarningDialog, {
        disableClose: true,
        panelClass: 'smt-excel-export-warning-dialog',
        width: '650px'
      });
    }

    return this.dialog.open(ExcelExportDialog, {
      data: {
        selection: config.selection,
        totalItems: config.totalItems
      },
      panelClass: 'smt-excel-export-dialog',
      width: '650px',
    });
  }

  private exceedsExportLimit(config: ActionDialogConfig): boolean {
    const maxItemsExport = this.aldenService.getAppConfigProp(APP_CONFIG_MAX_ITEMS_EXPORT);
    if (maxItemsExport === undefined || maxItemsExport === null) {
      return false;
    }

    const itemsToExport = config.selection?.count > 0
      ? config.selection.count
      : config.totalItems ?? 0;

    return Number(itemsToExport) > Number(maxItemsExport);
  }

  /**
   * Abre el cuadro de diálogo para descargar todos los datos seleccionados.
   * Este método centraliza la lógica de apertura del cuadro de diálogo para la descarga de datos.
   *
   * @param config - Configuración que contiene la selección de datos a descargar.
   * @returns Un MatDialogRef con la instancia del cuadro de diálogo para la descarga de datos.
   */
  private async smtDownloadAllActionDialog(config: ActionDialogConfig): Promise<void> {
    const maxSizeDownload = Number(this.aldenService.getAppConfigProp(APP_CONFIG_MAX_SIZE_DOWNLOAD));
    let exceedsLimit = false;
    if (config.selection?.count > 0) {
      if (!Number.isFinite(maxSizeDownload) || maxSizeDownload <= 0) {
        this.openDownloadAllDialog(config);
        return;
      }

      const selectedSizeInBytes = (config.selection.nodes ?? []).reduce((total, node) => {
        const sizeInBytes = Number(node?.entry?.content?.sizeInBytes);
        return total + (Number.isFinite(sizeInBytes) ? sizeInBytes : 0);
      }, 0);
      exceedsLimit = selectedSizeInBytes > maxSizeDownload * 1024 * 1024;

      if (exceedsLimit) {
        this.openDownloadSizeWarningDialog(maxSizeDownload);
      } else {
        this.openDownloadAllDialog(config);
      }
      return;
    }

    const query = this.customListQueryBuilderService.getCurrentQuery();
    if (!query) {
      this.openDownloadAllDialog(config);
      return;
    }

    const loadingDialog = this.dialog.open(SMTLoadingDialogComponent, {
      minWidth: '250px',
      disableClose: true,
      data: { title: 'ALDEN.DIALOGS.DOWNLOAD_ALL_DOCUMENTS.PREPARING' }
    });
    try {
      const nodeIds = await this.smtActionsService.downloadAllDocsByQuery(query).toPromise();
      this.openDownloadAllDialog(config, nodeIds);
    } catch (err) {
      const errorData = this.parseDownloadError(err?.message);
      if (errorData?.err === 'Oversize') {
        this.openDownloadSizeWarningDialog(Number(errorData.maxSize) || maxSizeDownload);
      } else {
        this.store.dispatch(new SnackbarErrorAction('Error interno'));
      }
      console.error(err);
    } finally {
      loadingDialog.close();
    }
  }

  private openDownloadSizeWarningDialog(limit: number): MatDialogRef<any> {
    return this.dialog.open(DownloadSizeWarningDialog, {
      width: '600px',
      disableClose: true,
      panelClass: 'smt-download-size-warning-dialog',
      data: { limit }
    });
  }

  private parseDownloadError(errMessage: string): any {
    if (!errMessage) {
      return null;
    }

    try {
      const errorData = JSON.parse(errMessage);
      if (errorData?.err) {
        return errorData;
      }

      const nestedMessage = errorData?.message;
      const payloadStart = typeof nestedMessage === 'string' ? nestedMessage.indexOf('{') : -1;
      return payloadStart >= 0 ? JSON.parse(nestedMessage.substring(payloadStart)) : null;
    } catch {
      return null;
    }
  }

  private openDownloadAllDialog(config: ActionDialogConfig, nodeIds?: string[]): MatDialogRef<DownloadAllDialog> {
    return this.dialog.open(DownloadAllDialog, {
      data: {
        selection: config.selection,
        ...(nodeIds !== undefined ? { nodeIds } : {})
      },
      panelClass: 'smt-download-all-dialog',
      width: '650px'
    });
  }

}
